/**
 * Données de l'accueil unique : chaque bloc a sa requête, toutes lancées en
 * même temps (aucune n'attend une autre) et chacune derrière le droit qui
 * l'ouvre côté serveur. Hors ligne, la journée de terrain se reconstruit
 * depuis le cache synchronisé, avec la date de l'appareil.
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../../core/api.js';
import { listEntities } from '../../offline/sync.js';
import { cachedVisits, type CachedInterventionRow } from '../../offline/cached-records.js';
import { isSameLocalDay, overlapsLocalDay } from './local-day.js';

export interface Exceptions {
  day?: string;
  urgences: {
    id: string; intervention_id: string | null; service_request_id?: string | null;
    title: string; status: string; created_at?: string;
    /** Faux pendant une astreinte qui couvre l'urgence, pour qui n'en est pas ni ne gère le planning. */
    can_handle?: boolean;
  }[];
  conflits: {
    id: string; full_name: string; detected_at?: string;
    item_a_kind?: string; item_a_id?: string; item_b_kind?: string; item_b_id?: string;
  }[];
  stockBas: { id: string; reference: string; label: string }[];
  impayes: {
    id: string; number: string; display_name: string; due_date?: string;
    total_ttc_cents: number | string; reste_a_payer_cents?: number | string;
  }[];
  conformite: { id: string; serial_number: string | null; customer_name: string; next_leak_check_on: string }[];
  anomalies: { id: string; kind: string; reference: string; label?: string; article_id?: string; created_at?: string }[];
  slaAlertes: {
    id: string; kind: string; due_at: string; contract_label: string | null;
    contract_id?: string | null; intervention_id?: string | null;
  }[];
  syncConflicts: { id: string; entity_kind: string; field: string | null; created_at?: string }[];
  devisEnAttente?: { id: string; number: string; display_name: string; sent_at: string }[];
  rapportsInacheves?: { id: string; display_name: string; starts_at?: string }[];
  demandesAQualifier?: {
    id: string; reference: string; requester_name: string; perceived_urgency: string; created_at: string;
    customer_name: string | null; site_label: string | null;
  }[];
  nonAffectees?: {
    id: string; type: string; starts_at: string; time_approx_minutes: number | null;
    customer_name: string; site_label: string;
  }[];
  retards?: {
    id: string; type: string; starts_at: string; time_approx_minutes: number | null;
    assignee_name: string; customer_name: string; site_label: string;
  }[];
  oublis?: ForgottenEnd[];
}

export interface ForgottenEnd {
  id: string;
  intervention_id: string;
  segment: 'trajet' | 'travail' | 'pause';
  segment_started_at: string;
  stopped_at: string;
  technician: string | null;
  customer_name: string;
  site_label: string;
}

export interface Reminder {
  id: string;
  title: string;
  detail: string;
  to: string;
  action: string;
}

export interface DayIntervention {
  id: string;
  type: string;
  priority: string;
  status: string;
  starts_at: string;
  customer_name: string;
  site_label: string;
  site_address: string;
  latitude?: number | null;
  longitude?: number | null;
  local_contact_phone: string | null;
  customer_phone?: string | null;
  time_approx_minutes?: number | null;
}

export interface MyDay {
  day?: string;
  interventions: DayIntervention[];
  events: { id: string; kind: string; title: string; starts_at: string; ends_at?: string; time_approx_minutes?: number | null }[];
  urgences: {
    id: string; title: string; status: string; intervention_id?: string | null; created_at?: string;
    /** Faux pendant une astreinte qui couvre l'urgence, pour qui n'en est pas ni ne gère le planning. */
    can_handle?: boolean;
  }[];
  incomplets: { id: string; intervention_id: string; starts_at?: string; customer_name?: string; site_label?: string }[];
  reservations: { id: string; intervention_id?: string; reference: string; label: string; qty_milli: number }[];
  oublis?: ForgottenEnd[];
  /** Reconstruite depuis le cache de l'appareil (réseau absent). */
  offline?: boolean;
}

export interface TeamMember {
  id: string;
  name: string;
  avatar_url: string | null;
  avatar_file_id: string | null;
  status: 'absent' | 'en_route' | 'en_intervention' | 'disponible' | 'termine' | 'sans_intervention';
  current: TeamVisit | null;
  next: TeamVisit | null;
  late: boolean;
  total: number;
  done: number;
  absence: { kind: string; title: string } | null;
}

export interface TeamVisit {
  id: string;
  type: string;
  status: string;
  starts_at: string;
  time_approx_minutes: number | null;
  customer_name: string;
  site_label: string;
}

export interface TeamToday {
  day: string;
  people: TeamMember[];
  totals: { interventions: number; active: number; done: number; unassigned: number };
}

export interface Reperes {
  day: string;
  impayes: { count: number; resteDuCents: number } | null;
  devisEnAttente: { count: number; totalTtcCents: number } | null;
  chiffreAffairesMois: { from: string; to: string; netHtCents: number } | null;
}

export interface ObjectivePerson {
  id: string;
  name: string;
  counts: { tenus: number; en_cours: number; bloques: number; non_atteints: number };
  items: {
    id: string; title: string; status: 'a_faire' | 'faite'; due_at: string; done_at: string | null;
    blocked_reason: string | null; state: string;
  }[];
}

const MINUTE = 60_000;

/**
 * Droits qui ouvrent les vues d'ensemble du bureau (exceptions, repères), les
 * mêmes que `hasOfficeAccess` côté serveur : sans l'un d'eux, l'accueil ne les
 * demande pas plutôt que d'afficher un refus.
 */
export const OFFICE_HOME_CAPABILITIES = [
  'planning.view_all', 'invoice.view', 'quote.manage', 'stock.manage', 'customer.manage',
] as const;

export function useExceptions(enabled: boolean) {
  return useQuery({
    queryKey: ['dashboard-exceptions'],
    queryFn: () => api<Exceptions>('/dashboard/exceptions'),
    enabled,
    // Gardé malgré le défaut `offlineFirst` : sans repli sur l'appareil, ses nouveaux
    // essais seraient mis en pause et l'écran attendrait en silence ; ainsi il dit l'échec.
    networkMode: 'always',
    refetchInterval: MINUTE,
  });
}

export function useReminders(userId: string | undefined) {
  return useQuery({
    queryKey: ['daily-reminders', userId],
    queryFn: () => api<Reminder[]>('/dashboard/reminders'),
    enabled: Boolean(userId),
    staleTime: MINUTE,
  });
}

export function useTeamToday(enabled: boolean) {
  return useQuery({
    queryKey: ['dashboard-team-today'],
    queryFn: () => api<TeamToday>('/dashboard/team-today'),
    enabled,
    // Pas de repli sur l'appareil : hors ligne, l'échec se dit (voir `useExceptions`).
    networkMode: 'always',
    refetchInterval: MINUTE,
  });
}

export function useReperes(enabled: boolean) {
  return useQuery({
    queryKey: ['dashboard-reperes'],
    queryFn: () => api<Reperes>('/dashboard/reperes'),
    enabled,
    // Pas de repli sur l'appareil : hors ligne, l'échec se dit (voir `useExceptions`).
    networkMode: 'always',
    staleTime: 5 * MINUTE,
  });
}

/** Objectifs donnés aujourd'hui, par personne : même requête que la page Tâches. */
export function useObjectivesToday(enabled: boolean) {
  return useQuery({
    queryKey: ['tasks', 'objectives-today'],
    queryFn: () => api<{ day: string; people: ObjectivePerson[] }>('/tasks/objectives/today'),
    enabled,
    refetchInterval: MINUTE,
  });
}

interface CachedEvent {
  id: string; user_id: string; kind: string; title: string; starts_at: string; ends_at: string;
  time_approx_minutes?: number | null; approval_status?: string | null;
}

/** Interventions du jour civil de `now` (heure de l'appareil) d'une personne, hors annulées. */
export function todaysInterventions<T extends Pick<CachedInterventionRow, 'starts_at' | 'status' | 'assignee_id'>>(
  rows: readonly T[], userId: string, now: Date, timeZone?: string,
): T[] {
  return rows
    .filter((row) => row.assignee_id === userId && row.status !== 'annulee'
      && Boolean(row.starts_at) && isSameLocalDay(row.starts_at ?? '', now, timeZone))
    .sort((a, b) => (a.starts_at ?? '').localeCompare(b.starts_at ?? ''));
}

/** Journée reconstruite depuis le cache synchronisé de l'appareil (réseau absent). */
export async function offlineMyDay(userId: string, now = new Date()): Promise<MyDay> {
  const visits = await cachedVisits((rows) => todaysInterventions(rows, userId, now));
  const interventions: DayIntervention[] = visits.map(({ intervention: row, customer, site }) => ({
    id: row.id, type: row.type, priority: row.priority, status: row.status, starts_at: row.starts_at ?? '',
    time_approx_minutes: row.time_approx_minutes ?? null,
    customer_name: customer?.display_name ?? '',
    site_label: site?.label ?? '',
    site_address: site?.address ?? '',
    local_contact_phone: site?.local_contact_phone ?? null,
    customer_phone: customer?.phone ?? null,
    latitude: site?.latitude ?? null,
    longitude: site?.longitude ?? null,
  }));
  const events = (await listEntities<CachedEvent>('schedule_event'))
    .filter((event) => event.user_id === userId && event.approval_status !== 'refuse'
      && overlapsLocalDay(event.starts_at, event.ends_at, now))
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
    .map(({ id, kind, title, starts_at, ends_at, time_approx_minutes }) => ({
      id, kind, title, starts_at, ends_at, time_approx_minutes: time_approx_minutes ?? null,
    }));
  return { interventions, events, urgences: [], incomplets: [], reservations: [], oublis: [], offline: true };
}

export function useMyDay(userId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['my-day'],
    queryFn: async (): Promise<MyDay> => {
      try {
        return await api<MyDay>('/dashboard/my-day');
      } catch (error) {
        // Hors ligne : la journée vient du cache synchronisé (Q22), datée par l'appareil.
        if (error instanceof ApiError && error.code === 'network_error' && userId) return offlineMyDay(userId);
        throw error;
      }
    },
    enabled: enabled && Boolean(userId),
    // Sans réseau, le premier essai part quand même (défaut `offlineFirst`) et
    // tombe sur le cache de l'appareil.
    refetchInterval: MINUTE,
  });
}
